import React from 'react';
import {
  AdjustmentsHorizontalIcon,
  BeakerIcon,
  ChartBarIcon,
  CubeTransparentIcon,
  FingerPrintIcon,
  ArrowTrendingUpIcon,
  FolderIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';

const ICONS = {
  encapsulation: BeakerIcon,
  size: ChartBarIcon,
  batch: FolderIcon,
  pressure: ArrowTrendingUpIcon,
  stability: AdjustmentsHorizontalIcon,
  riboGreen: CubeTransparentIcon,
  mapping: FingerPrintIcon,
};

const TILE_THEMES = {
  lapisGradient: {
    container: 'border border-[#3F648D33] bg-[linear-gradient(128deg,#ffffff_0%,#ffffff_60%,#3F648D1f_100%)]',
    iconBg: 'bg-[#3F648D20]',
    iconColor: 'text-[#3F648D]',
    titleColor: 'text-[#3F648D]',
    bodyColor: 'text-[#2C2C2C]',
    button: 'bg-[#3F648D] text-white hover:bg-[#3F648D]/90',
  },
  lapisUpperRight: {
    container: 'border border-[#3F648D33] bg-[linear-gradient(300deg,#ffffff_0%,#ffffff_65%,#3F648D28_100%)]',
    iconBg: 'bg-white',
    iconColor: 'text-[#3F648D]',
    titleColor: 'text-[#3F648D]',
    bodyColor: 'text-[#2C2C2C]',
    button: 'bg-[#3F648D] text-white hover:bg-[#3F648D]/90',
  },
  orangeGradient: {
    container: 'border border-[#E46E0033] bg-[linear-gradient(130deg,#ffffff_0%,#ffffff_60%,#E46E0020_100%)]',
    iconBg: 'bg-[#FFE5CF3d]',
    iconColor: 'text-[#E46E00]',
    titleColor: 'text-[#E46E00]',
    bodyColor: 'text-[#2C2C2C]',
    button: 'bg-[#E46E00] text-white hover:bg-[#E46E00]/90',
  },
  orangeUpperRight: {
    container: 'border border-[#E46E0033] bg-[linear-gradient(305deg,#ffffff_0%,#ffffff_62%,#FFE5CF80_100%)]',
    iconBg: 'bg-white',
    iconColor: 'text-[#E46E00]',
    titleColor: 'text-[#E46E00]',
    bodyColor: 'text-[#2C2C2C]',
    button: 'bg-[#E46E00] text-white hover:bg-[#E46E00]/90',
  },
  greenGradient: {
    container: 'border border-[#C0DE2533] bg-[linear-gradient(155deg,#ffffff_0%,#ffffff_55%,#C0DE2526_100%)]',
    iconBg: 'bg-[#C0DE2530]',
    iconColor: 'text-[#2C2C2C]',
    titleColor: 'text-[#3F648D]',
    bodyColor: 'text-[#2C2C2C]',
    button: 'bg-[#C0DE25] text-[#2C2C2C] hover:bg-[#C0DE25]/90',
  },
  greenUpperRight: {
    container: 'border border-[#C0DE2533] bg-[linear-gradient(310deg,#ffffff_0%,#ffffff_60%,#C0DE2530_100%)]',
    iconBg: 'bg-white',
    iconColor: 'text-[#2C2C2C]',
    titleColor: 'text-[#3F648D]',
    bodyColor: 'text-[#2C2C2C]',
    button: 'bg-[#C0DE25] text-[#2C2C2C] hover:bg-[#C0DE25]/90',
  },
};

const DEFAULT_THEME = {
  container: 'border border-[#F0F0F0] bg-white',
  iconBg: 'bg-[#F0F0F0]',
  iconColor: 'text-[#143148]',
  titleColor: 'text-[#143148]',
  bodyColor: 'text-[#2C2C2C]',
  button: 'bg-[#143148] text-white hover:bg-[#143148]/90',
};

export function PipelineTile({
  title,
  description,
  icon,
  variant,
  onSelect,
  ctaLabel = 'Open',
  secondaryCtaLabel,
  onSecondaryAction,
  onQuickRun,
}) {
  const Icon = ICONS[icon] || QuestionMarkCircleIcon;
  const theme = TILE_THEMES[variant] || DEFAULT_THEME;

  return (
    <div
      className={`
        flex-1 rounded-2xl p-6 shadow-soft transition-all duration-200
        hover:-translate-y-1 hover:shadow-lg
        ${theme.container}
      `}
    >
      <div className="mb-4 flex items-center space-x-3">
        <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${theme.iconBg}`}>
          <Icon className={`h-6 w-6 ${theme.iconColor}`} />
        </span>
        <h3 className={`text-xl font-semibold ${theme.titleColor}`}>{title}</h3>
      </div>
      <p className={`mb-6 text-sm leading-relaxed ${theme.bodyColor}`}>{description}</p>
      <div className="flex flex-col gap-2">
        <button
          onClick={onSelect}
          className={`
            inline-flex items-center justify-center rounded-xl px-5 py-2 text-sm font-medium shadow
            transition-colors duration-200 ${theme.button}
          `}
        >
          {ctaLabel}
        </button>
        {secondaryCtaLabel && onSecondaryAction && (
          <button
            onClick={onSecondaryAction}
            className="text-sm font-semibold text-[#3F648D] underline underline-offset-2 hover:text-[#2c4a6b] text-left"
          >
            {secondaryCtaLabel}
          </button>
        )}
        {onQuickRun && (
          <button
            onClick={onQuickRun}
            className="text-xs font-semibold text-slate-500 hover:text-slate-700 text-left"
          >
            Quick run from selector
          </button>
        )}
      </div>
    </div>
  );
}
